import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";

import {
  badgeFor,
  boardRows,
  departurePage,
  departureDetail,
  departsCell,
  EXPORT_HEADER,
  exportRow,
  isFull,
  occupancyCell,
  ORDER_CHOICES,
  PER_PAGE_CHOICES,
  STATUS_FILTERS,
  tilesFor,
  type BoardRow,
  type DepartureOrder,
  type DepartureRow,
  type RouteRow,
} from "../departures.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Departures: every scheduled journey, how full it is, and what state it is in.
 *
 * **There is no design file for this screen**, and that is worth saying first. Every other page on
 * this console is built pixel for pixel from `19 WP18 Dispatch Operations.dc.html`; scheduled
 * departures were asked for after that bundle was cut. So this is built in the same language — the
 * same page chrome, the same grid chrome and toolbar, the same state badges and tokens — rather
 * than copied, and `departures.ts` states the choices that had no precedent.
 *
 * **This page is what deploys the seat-booking spine.** The deployment set is the closure of
 * services a buildable console page cites, and until now nothing cited MOB-04 — ADR 0100 §6 calls
 * that shipping dark, and it means there was no container, no route and no credential for any of
 * it. Adding this page brings MOB-04 into the estate, and with it DRV-04's seat layout and
 * MOB-01's sale mode, which are the two things it composes.
 *
 * **What it does not do yet.** There is no seat picker: holding and selling a seat are a
 * customer's actions on a channel that does not exist, and the two operations that do them are
 * MOB-04's `departure:book` and `departure:sell`. This board is the operator's half — see what is
 * running, see how full it is, and schedule the next one.
 */
export interface DeparturesProps {
  readonly transport: Transport;
  /** Innermost last: tenant, section, page. */
  readonly crumbs?: readonly string[];
  /** What this operator holds, which decides whether the form sends or explains. */
  readonly scopes?: ReadonlySet<string>;
}

/*
 * No `tenantId` and no `now`, unlike most boards here.
 *
 * The transport stamps the tenant on every request, so a component taking it again would be a
 * second source for one fact. And nothing on this board ages: every instant it draws arrives on
 * the row, so there is no clock for a test to pin.
 */

const WRITE_SCOPE = "departure:write";
const departuresApi = services.mobility__scheduled_departure;

/** One page is every route a tenant runs and every departure a board draws. */
const PER_FETCH = "200";

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

interface Loaded {
  readonly departures: readonly DepartureRow[];
  readonly routes: readonly RouteRow[];
}

export function Departures({ transport, crumbs, scopes }: DeparturesProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<DepartureRow["status"] | undefined>(undefined);
  const [order, setOrder] = useState<DepartureOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(PER_PAGE_CHOICES[0] ?? 10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = departuresApi.createScheduledDepartureClient(transport);
    try {
      /*
       * Together, because the board is one reading. Departures drawn against a route list taken a
       * moment later would name the wrong route on exactly the row somebody just added.
       */
      const [departures, routes] = await Promise.all([
        client.listDepartures(PER_FETCH, undefined, undefined),
        client.listRoutes(PER_FETCH, undefined),
      ]);
      setLoaded({ departures: departures.departures, routes: routes.routes });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["departure:read"]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Dismiss the export menu on Escape or a click outside it, as every other grid here does. */
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const rows = useMemo(
    () => (loaded === undefined ? undefined : boardRows(loaded.departures, loaded.routes)),
    [loaded],
  );
  /*
   * The tiles count every departure, never the filtered page. A `Boarding` tile that moved when
   * somebody typed in the search box would be a different claim about the estate per keystroke.
   */
  const tiles = useMemo(() => tilesFor(rows ?? []), [rows]);
  const view = useMemo(
    () =>
      departurePage(rows ?? [], {
        search,
        ...(status === undefined ? {} : { status }),
        order,
        page,
        perPage,
      }),
    [rows, search, status, order, page, perPage],
  );
  /* Found across every departure rather than the visible page, so paging does not close a panel. */
  const selected = useMemo(
    () => (rows ?? []).find((row) => row.departureId === selectedId),
    [rows, selectedId],
  );

  /*
   * `undefined` scopes is a caller that does not know, drawn as able to act. Hiding a control from
   * somebody who holds the scope is how a permission question becomes a bug report about a missing
   * feature.
   */
  const viewOnly = scopes !== undefined && !scopes.has(WRITE_SCOPE);

  const clearFilters = (): void => {
    setSearch("");
    setStatus(undefined);
    setPage(1);
  };

  const state = failure ?? stateForList(rows, {});

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="departures__head">
      <span>Route</span>
      <span>Departs</span>
      <span>Seats</span>
      <span className="departures__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      <section className="page departures">
        <PageHeading
          crumbs={crumbs ?? []}
          source="MOB-04"
          title="Departures"
          description="Every scheduled journey, how full it is, and what state it is in."
          action={
            <button
              type="button"
              className="departures__primary"
              /*
               * Drawn and refused, which is this console's treatment for a command whose form it
               * cannot fill. MOB-04's schedule takes a route, a vehicle, a product, a time **and
               * the vehicle's whole seat map** — that last one is DRV-04's layout, and a form that
               * collected it by hand would be asking an operator to retype a description of a bus
               * the platform already holds. The screen that composes those three reads is the one
               * this button is waiting on; Q-250 records it.
               */
              disabled
              title="Scheduling a departure needs a route, a vehicle, a product sold by the seat, and that vehicle's seat layout from DRV-04. This console reads none of the last three yet, and a form that asked an operator to retype a seat map would be asking them for something the platform already knows (Q-250)."
            >
              <span aria-hidden="true" className="departures__primary-glyph">
                more_time
              </span>
              Schedule a departure
            </button>
          }
          {...(viewOnly ? { aside: <span className="departures__view-only">View only</span> } : {})}
        />

        <div className="departures__tiles">
          <div className="departures__tile">
            <span className="departures__tile-label">Scheduled</span>
            <span className="departures__tile-value">{tiles.scheduled}</span>
            <StateBadge
              classKey="waiting"
              label="not yet boarding"
              code="MOB-04"
              status="scheduled"
            />
          </div>
          <div className="departures__tile">
            <span className="departures__tile-label">Boarding</span>
            <span className="departures__tile-value">{tiles.boarding}</span>
            <StateBadge classKey="active" label="doors open" code="MOB-04" status="boarding" />
          </div>
          <div className="departures__tile">
            <span className="departures__tile-label">Full</span>
            <span className="departures__tile-value">{tiles.full}</span>
            <StateBadge
              classKey={tiles.full > 0 ? "caution" : "done"}
              label="no seat free or held"
              code="MOB-04"
              status="full"
              title="Counted over departures that have not left, because a departed bus is one nobody can act on. Full means no seat is free and none is held — a departure with holds outstanding is not full, because those holds expire."
            />
          </div>
          <div className="departures__tile">
            <span className="departures__tile-label">Seats held</span>
            <span className="departures__tile-value">{tiles.seatsHeld}</span>
            <StateBadge
              classKey="waiting"
              label="being paid for"
              code="MOB-04"
              status="held"
              title="Seats taken out of sale while somebody pays. A hold lapses on its own after fifteen minutes and the seat goes back into the count, so this number falls without anybody doing anything."
            />
          </div>
        </div>

        {/*
          Design rule 4's toolbar, working over what the board holds.

          Client-side, and that is a property of the two reads rather than a shortcut: MOB-04's
          listings take a cursor and an optional `from` and nothing else — no search, no status
          filter, no sort to delegate to.
        */}
        <div className="departures__panel">
          <div className="departures__toolbar">
            <span className="departures__search">
              <span aria-hidden="true" className="departures__search-glyph">
                search
              </span>
              <input
                id="departures-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="departures-status"
              aria-label="Filter by state"
              value={status ?? ""}
              onChange={(event) => {
                setStatus(
                  event.target.value === ""
                    ? undefined
                    : (event.target.value as DepartureRow["status"]),
                );
                setPage(1);
              }}
            >
              <option value="">All states</option>
              {STATUS_FILTERS.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <select
              id="departures-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as DepartureOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="departures__export" ref={exportRegion}>
              <button
                type="button"
                className="departures__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="departures__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="departures__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="departures__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        /* Every matching row, not the page on screen. */
                        exportRows(view.all, choice.format);
                      }}
                    >
                      <span aria-hidden="true" className="departures__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={state}
            purpose="Scheduled journeys appear here as soon as one is scheduled."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "departures__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}

            <div className="departures__rows">
              {view.rows.map((row) => {
                const badge = badgeFor(row.status);
                const departs = departsCell(row.departsAt);
                const seats = occupancyCell(row);
                return (
                  <div
                    key={row.departureId}
                    data-selected={row.departureId === selectedId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={row.departureId === selectedId}
                    aria-label={`Open departure ${row.routeName}`}
                    onClick={() => setSelectedId(row.departureId)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedId(row.departureId);
                    }}
                    /*
                     * A full departure takes the caution tint. It is the one row on this board an
                     * operator may need to act on — another vehicle, or a second departure — and
                     * the design's rule is that two of nine classes tint a row.
                     */
                    className={
                      isFull(row) && row.status !== "departed" && row.status !== "cancelled"
                        ? "departures__row departures__row--full"
                        : "departures__row"
                    }
                  >
                    <span
                      className="departures__route"
                      {...(row.routeMissing
                        ? {
                            title:
                              "MOB-04 answered a route identifier its own route listing does not name, so the identifier is shown rather than a blank.",
                          }
                        : {})}
                    >
                      {row.routeName}
                    </span>
                    <span className="departures__departs" title={departs.title}>
                      {departs.text}
                    </span>
                    <span className="departures__seats" title={seats.title}>
                      {seats.text}
                    </span>
                    <span className="departures__state">
                      <StateBadge
                        classKey={badge.classKey}
                        label={badge.label}
                        code="MOB-04"
                        status={row.status}
                      />
                    </span>
                  </div>
                );
              })}
            </div>

            {view.total === 0 ? (
              <div className="departures__no-match">
                <span aria-hidden="true" className="departures__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="departures__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}

            <div className="departures__pager">
              <span>
                {view.total === 0
                  ? "No matching departures"
                  : `Showing ${String((page - 1) * perPage + 1)}–${String(Math.min(page * perPage, view.total))} of ${String(view.total)}`}
              </span>
              <span className="departures__pager-controls">
                <select
                  id="departures-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="departures__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>
        </div>

        <p className="departures__note">
          <span aria-hidden="true" className="departures__note-glyph">
            event_seat
          </span>
          Every seat sold on a departure becomes its own trip, joined to the others by this
          departure. A held seat is not a sale: a hold lapses on its own after fifteen minutes.
        </p>
      </section>

      {selected === undefined ? null : (
        <RecordDetail
          record={departureDetail(selected)}
          open
          onClose={() => setSelectedId(undefined)}
        />
      )}
    </div>
  );
}

/**
 * Write the matching departures out, in the three formats the design's menu offers.
 *
 * The same shape `ActiveRides` and the surge board use: a real CSV, an Excel-readable HTML table,
 * and the browser's own print dialog for the PDF.
 */
function exportRows(rows: readonly BoardRow[], format: ExportFormat): void {
  const header = [...EXPORT_HEADER];
  const body = rows.map((row) => [...exportRow(row)]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "departures.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "departures.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>departures</title></head><body><h2 style="font-family:sans-serif">Departures</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
